import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import {
  IdentifiedUser,
  useLaunchDarkly,
} from "integrations/launch-darkly/LaunchDarklyContext";
import { FeatureFlags } from "integrations/launch-darkly/utils";
import { GlobalLoading } from "components/loading";
import { toast } from "utils/toast";
import { useAuth } from "./AuthContext";

interface FeatureFlagContextValue {
  features: FeatureFlags;
}

export const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(
  null,
);

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const {
    state: { authUser },
  } = useAuth();
  const { t } = useTranslation(["error"]);

  const ldUser = useMemo<IdentifiedUser | null>(
    () =>
      authUser
        ? {
            key: authUser.uid,
            email: authUser.email ?? undefined,
            name: authUser.displayName ?? undefined,
          }
        : null,
    [authUser],
  );
  const { features, configured, identified, error } = useLaunchDarkly(ldUser);

  const toastedError = useRef<Error | null>(null);
  useEffect(() => {
    if (authUser && error && toastedError.current !== error) {
      toastedError.current = error;
      toast.error(t("launchDarklyUnavailable"));
    }
  }, [error, t, authUser]);

  const value = useMemo(() => ({ features }), [features]);

  const flagsReady = !configured || !authUser || identified;

  // Only the first identification blocks: unmounting rendered UI kills the
  // email-link sign-in mid-flow, so transitions render through on defaults.
  const shownChildren = useRef(false);
  if (!flagsReady && !error && !shownChildren.current) {
    return <GlobalLoading />;
  }
  shownChildren.current = true;

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagProvider",
    );
  }
  return context;
};
